import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { QuickActionsMenuComponent } from '../../../shared/components/quick-actions-menu/quick-actions-menu.component';
import {
  EntityPickerComponent,
  PickerOption,
  PickerSearchPage,
} from '../../../shared/components/form/entity-picker/entity-picker.component';
import { TaskApiService } from '../services/task-api.service';
import { TaskCommentApiService } from '../services/task-comment-api.service';
import { TaskSubResourcesApiService } from '../services/task-sub-resources-api.service';
import { TaskCatalogApiService } from '../services/task-catalog-api.service';
import { TaskErrorService } from '../services/task-error.service';
import { TaskDrawerService } from '../services/task-drawer.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { DealApiService } from '../../deals/services/deal-api.service';
import { LeadApiService } from '../../leads/services/lead-api.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import {
  TaskResponse,
  TaskTypeResponse,
  TaskStatusResponse,
  TaskPriorityResponse,
  TaskCategoryResponse,
  TaskCommentResponse,
  TaskFollowerResponse,
  TaskDependencyResponse,
  TaskContactResponse,
  TaskCancelReasonResponse,
  TaskTimelineEventResponse,
} from '../models/task.model';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditApiService } from '../../audit/services/audit-api.service';

type TabKey = 'info' | 'subtasks' | 'comments' | 'dependencies' | 'timeline';

@Component({
  selector: 'app-task-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ModalComponent,
    HasPermissionDirective,
    EntityPickerComponent,
    AuditTimelineComponent,
    QuickActionsMenuComponent,
  ],
  templateUrl: './task-detail-modal.component.html',
})
export class TaskDetailModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() taskId?: number;
  @Output() closed = new EventEmitter<void>();
  @Output() changed = new EventEmitter<TaskResponse>();

  task?: TaskResponse;
  loading = false;
  error: string | null = null;

  /** INC 11 — última tarea cargada; sobrevive al cierre del modal para refrescar subtareas. */
  private lastLoadedTaskId: number | null = null;

  activeTab: TabKey = 'info';

  // Sub-recursos
  subtasks: TaskResponse[] = [];
  comments: TaskCommentResponse[] = [];
  followers: TaskFollowerResponse[] = [];
  dependencies: TaskDependencyResponse[] = [];
  contacts: TaskContactResponse[] = [];

  // Catálogos para traducción en timeline (Fase 1)
  types: TaskTypeResponse[] = [];
  statuses: TaskStatusResponse[] = [];
  priorities: TaskPriorityResponse[] = [];
  categories: TaskCategoryResponse[] = [];

  // Usuarios resueltos
  ownerUser: {
    fullname?: string;
    lastname?: string;
    mail?: string;
    email?: string;
  } | null = null;
  assignedUser: {
    fullname?: string;
    lastname?: string;
    mail?: string;
    email?: string;
  } | null = null;
  creatorUser: {
    fullname?: string;
    lastname?: string;
    mail?: string;
    email?: string;
  } | null = null;
  loadingUsers = false;

  // Asociaciones resueltas
  resolvedCompanyName = '';
  resolvedDealName = '';
  resolvedLeadName = '';
  resolvedParentTaskTitle = '';

  // UI: comentarios
  newCommentContent = '';

  // UI: cancel
  showCancelDialog = false;
  cancelReasons: TaskCancelReasonResponse[] = [];
  cancelReasonId?: number;
  cancelNotes = '';

  // INC 29 — agregar dependencia (tarea bloqueante)
  newBlockedBy: PickerOption | null = null;
  addingDependency = false;
  searchTaskFn = (term: string, page: number) => this.searchTasksFn(term, page);

  // Audit timeline
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private taskApi: TaskApiService,
    private commentApi: TaskCommentApiService,
    private subApi: TaskSubResourcesApiService,
    private catalogApi: TaskCatalogApiService,
    private taskErrorService: TaskErrorService,
    private drawerService: TaskDrawerService,
    private authApiService: AuthApiService,
    private authService: AuthService,
    private datetimeService: DatetimeService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private leadApi: LeadApiService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private auditApi: AuditApiService,
    public router: Router,
  ) {}

  ngOnChanges(c: SimpleChanges): void {
    if (
      (c['isOpen']?.currentValue || c['taskId']?.currentValue) &&
      this.isOpen &&
      this.taskId
    ) {
      this.activeTab = 'info';
      this.loadAll();
    }
    if (c['isOpen']?.currentValue === false) {
      this.task = undefined;
      this.subtasks = [];
      this.comments = [];
      this.followers = [];
      this.dependencies = [];
      this.contacts = [];
      this.ownerUser = null;
      this.assignedUser = null;
      this.creatorUser = null;
      this.resolvedCompanyName = '';
      this.resolvedDealName = '';
      this.resolvedLeadName = '';
      this.resolvedParentTaskTitle = '';
      this.timelineError = '';
      this.timelineLogs = [];
    }
  }

  ngOnInit(): void {
    // INC 11 — Suscribirse a los eventos de guardado del drawer para refrescar subtareas
    // sin recargar la página. No dependemos de `isOpen`: al crear una subtarea el flujo
    // cierra el modal de detalle (por z-index del drawer), por lo que el modal puede estar
    // cerrado cuando llega el evento. Usamos `lastLoadedTaskId` para que el refresco quede
    // listo y, si el modal sigue abierto, se vea de inmediato.
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((savedTask) => {
        const currentId = this.taskId ?? this.lastLoadedTaskId;
        if (currentId == null || savedTask == null) return;

        // Si se guardó la tarea actual o una de sus subtareas
        if (
          savedTask.id === currentId ||
          savedTask.idParentTask === currentId
        ) {
          if (savedTask.idParentTask === currentId)
            this.refreshSubtasks(currentId);
          // Refrescar la tarea principal para actualizar los contadores de subtareas
          this.refreshTask(currentId);
        }
      });
  }

  /** INC 11 — recarga las subtareas de una tarea, aunque el modal esté cerrado. */
  private refreshSubtasks(taskId: number): void {
    this.taskApi
      .getSubtasks(taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (subtasks) => (this.subtasks = subtasks),
        error: () => {
          /* best-effort */
        },
      });
  }

  /** INC 11 — recarga la tarea principal (contadores de subtareas) sin emitir errores ruidosos. */
  private refreshTask(taskId: number): void {
    this.taskApi
      .getTaskById(taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (t) => {
          this.task = t;
          if (this.isOpen) {
            this.resolveAssociations();
            this.loadUsers();
          }
        },
        error: () => {
          /* best-effort */
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Carga =====

  private loadAll(): void {
    if (this.taskId == null) return;
    this.lastLoadedTaskId = this.taskId; // INC 11 — persiste para refrescar tras cerrar el modal
    this.loading = true;
    this.error = null;

    forkJoin({
      task: this.taskApi
        .getTaskById(this.taskId)
        .pipe(catchError(() => of(null))),
      subtasks: this.taskApi
        .getSubtasks(this.taskId)
        .pipe(catchError(() => of([] as TaskResponse[]))),
      followers: this.subApi
        .listFollowers(this.taskId)
        .pipe(catchError(() => of([] as TaskFollowerResponse[]))),
      dependencies: this.subApi
        .listDependencies(this.taskId)
        .pipe(catchError(() => of([] as TaskDependencyResponse[]))),
      contacts: this.subApi
        .listContacts(this.taskId)
        .pipe(catchError(() => of([] as TaskContactResponse[]))),
      reasons: this.catalogApi
        .getActiveCancelReasons()
        .pipe(catchError(() => of([] as TaskCancelReasonResponse[]))),
      activeTypes: this.catalogApi
        .getActiveTypes()
        .pipe(catchError(() => of([] as TaskTypeResponse[]))),
      activeStatuses: this.catalogApi
        .getActiveStatuses()
        .pipe(catchError(() => of([] as TaskStatusResponse[]))),
      activePriorities: this.catalogApi
        .getActivePriorities()
        .pipe(catchError(() => of([] as TaskPriorityResponse[]))),
      activeCategories: this.catalogApi
        .getActiveCategories()
        .pipe(catchError(() => of([] as TaskCategoryResponse[]))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (r) => {
          if (!r.task) {
            this.error = 'No se pudo cargar la tarea.';
            this.loading = false;
            return;
          }
          this.task = r.task;
          this.subtasks = r.subtasks;
          this.followers = r.followers;
          this.dependencies = r.dependencies;
          this.contacts = r.contacts;
          this.cancelReasons = r.reasons;
          this.cancelReasonId = r.reasons[0]?.id;
          this.types = r.activeTypes;
          this.statuses = r.activeStatuses;
          this.priorities = r.activePriorities;
          this.categories = r.activeCategories;
          this.loading = false;
          this.loadComments();
          this.loadUsers();
          this.resolveAssociations();
        },
        error: () => {
          this.error = 'No se pudo cargar el detalle.';
          this.loading = false;
        },
      });
  }

  /**
   * Resuelve los IDs de las asociaciones a sus nombres/títulos correspondientes.
   */
  private resolveAssociations(): void {
    if (!this.task) return;
    const businessId = this.authService.getCurrentUser()?.businessId ?? 1;

    const calls: { [key: string]: Observable<any> } = {};

    if (this.task.intIdCompany) {
      calls['company'] = this.empresaApi
        .getCompanyById(this.task.intIdCompany)
        .pipe(catchError(() => of(null)));
    }
    if (this.task.intIdDeal) {
      calls['deal'] = this.dealApi
        .getDealById(this.task.intIdDeal, businessId)
        .pipe(catchError(() => of(null)));
    }
    if (this.task.intIdLead) {
      calls['lead'] = this.leadApi
        .getLeadById(this.task.intIdLead, businessId)
        .pipe(catchError(() => of(null)));
    }
    if (this.task.idParentTask) {
      calls['parent'] = this.taskApi
        .getTaskById(this.task.idParentTask)
        .pipe(catchError(() => of(null)));
    }

    if (Object.keys(calls).length === 0) return;

    forkJoin(calls)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res['company'])
            this.resolvedCompanyName = res['company'].commercialName;
          if (res['deal']) this.resolvedDealName = res['deal'].name;
          if (res['lead']) this.resolvedLeadName = res['lead'].name;
          if (res['parent']) this.resolvedParentTaskTitle = res['parent'].title;
        },
        error: () => {}, // Errores silenciosos, se mantiene el ID como fallback en el HTML
      });
  }

  private loadComments(): void {
    if (this.taskId == null) return;
    this.commentApi
      .list(this.taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => (this.comments = page.content || []),
        error: () => (this.comments = []),
      });
  }

  private loadUsers(): void {
    const ownerId = this.task?.intIdUserOwner;
    const creatorId = this.task?.intIdUserCreator;
    if (!ownerId && !creatorId) return;

    this.loadingUsers = true;
    this.ownerUser = null;
    this.assignedUser = null;
    this.creatorUser = null;

    const currentUser = this.authService.getCurrentUser();
    type UserLike = {
      fullname?: string;
      lastname?: string;
      mail?: string;
      email?: string;
    } | null;

    const resolve = (id?: number): Observable<UserLike> => {
      if (id == null) return of<UserLike>(null);
      if (id === currentUser?.id) return of<UserLike>(currentUser as UserLike);
      return this.authApiService.getUserById(id).pipe(
        map((u) => u as UserLike),
        catchError(() => of<UserLike>(null)),
      );
    };

    if (ownerId === creatorId) {
      resolve(ownerId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((u: UserLike) => {
          this.ownerUser = u;
          this.assignedUser = u;
          this.creatorUser = u;
          this.loadingUsers = false;
        });
    } else {
      forkJoin([resolve(ownerId), resolve(creatorId)])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([o, c]: [UserLike, UserLike]) => {
          this.ownerUser = o;
          this.assignedUser = o;
          this.creatorUser = c;
          this.loadingUsers = false;
        });
    }
  }

  // ===== INC 29 — Dependencias (bloqueada por) =====

  private loadDependencies(): void {
    if (this.taskId == null) return;
    this.subApi
      .listDependencies(this.taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (deps) => (this.dependencies = deps),
        error: () => (this.dependencies = []),
      });
  }

  onBlockedByChange(opt: PickerOption | null): void {
    this.newBlockedBy = opt;
  }

  agregarDependencia(): void {
    if (this.task?.id == null || !this.newBlockedBy) return;
    const taskId = this.task.id;
    this.addingDependency = true;
    this.subApi
      .addDependency(taskId, { idTaskBlockedBy: this.newBlockedBy.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.addingDependency = false;
          this.newBlockedBy = null;
          this.loadDependencies();
          this.toast.created('Dependencia');
        },
        // 400 auto-dependencia, 404 no existe, 409 ya existe o ciclo → mostrar message del backend.
        error: (err: any) => {
          this.addingDependency = false;
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo agregar la dependencia.',
          );
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  async eliminarDependencia(d: TaskDependencyResponse): Promise<void> {
    if (this.task?.id == null) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Quitar dependencia',
      message: d.blockedByTaskTitle
        ? `¿Quitar "${d.blockedByTaskTitle}" como tarea bloqueante?`
        : '¿Quitar esta tarea bloqueante?',
      confirmText: 'Quitar',
    });
    if (!ok) return;
    this.subApi
      .removeDependency(this.task.id, d.idTaskBlockedBy)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadDependencies();
          this.toast.deleted('Dependencia');
        },
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo quitar la dependencia.',
          );
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  private searchTasksFn(
    term: string,
    page: number,
  ): Observable<PickerSearchPage> {
    // El backend filtra por businessId vía JWT. Excluimos la tarea actual del resultado.
    return this.taskApi
      .filterTasks({ searchTerm: term || undefined, page, size: 10 })
      .pipe(
        map((res) => ({
          items: (res.content || [])
            .filter((t) => t.id !== this.task?.id)
            .map(
              (t) =>
                ({
                  id: t.id,
                  label: t.title,
                  sublabel: t.status?.label,
                }) as PickerOption,
            ),
          totalPages: res.totalPages,
        })),
        catchError(() => of({ items: [], totalPages: 0 } as PickerSearchPage)),
      );
  }

  // ===== Tabs =====

  setTab(t: TabKey): void {
    this.activeTab = t;
    if (
      t === 'timeline' &&
      this.timelineLogs.length === 0 &&
      !this.loadingTimeline
    ) {
      this.loadTimeline(0);
    }
  }

  // ===== Acciones =====

  editar(): void {
    if (this.task?.id == null) return;
    // Cerramos el modal de detalle antes de abrir el drawer de edición para que
    // el drawer (z-drawer) no quede atrapado por debajo del modal (z-modal).
    this.cerrar();
    this.drawerService.open({ taskId: this.task.id });
  }

  async completar(): Promise<void> {
    if (this.task?.id == null) return;
    // INC 26 — ofrecer una nota final OPCIONAL al completar.
    const note = await this.confirmDialog.prompt({
      title: 'Completar tarea',
      message: 'Puedes agregar una nota final (opcional).',
      placeholder: 'Ej. resultado de la gestión, próximos pasos...',
      inputType: 'textarea',
      confirmText: 'Completar',
      required: false,
    });
    // prompt resuelve null si el usuario cancela; '' o texto si confirma.
    if (note === null) return;
    const id = this.task.id;
    this.taskApi
      .completeTask(id, { completionNotes: note.trim() || undefined })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (t) => {
          this.task = t;
          this.changed.emit(t);
          this.drawerService.notifySaved(t);
          this.loadAll();
          this.toast.success('Tarea completada');
        },
        // INC 28 (subtareas pendientes) / INC 29 (bloqueantes sin finalizar) / INC 35 (ya completada).
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo completar la tarea.',
          );
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  posponer(): void {
    if (this.task?.id == null) return;
    const due = new Date();
    due.setDate(due.getDate() + 1);
    this.taskApi
      .snoozeTask(this.task.id, {
        newDueDate: due.toISOString(),
        presetKey: 'TOMORROW',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (t) => {
          this.task = t;
          this.changed.emit(t);
          this.drawerService.notifySaved(t);
          this.loadAll();
        },
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo posponer la tarea.',
          );
          this.confirmDialog.alert({
            title: 'Error',
            message: msg,
            variant: 'danger',
          });
        },
      });
  }

  async reabrir(): Promise<void> {
    if (this.task?.id == null) return;
    const reason = await this.confirmDialog.prompt({
      title: 'Reabrir tarea',
      message: 'Indica el motivo por el cual se reabre esta tarea.',
      placeholder: 'Ej. requisito adicional del cliente',
      inputType: 'textarea',
      confirmText: 'Reabrir',
    });
    if (!reason) return;
    this.taskApi
      .reopenTask(this.task.id, { reason })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (t) => {
          this.task = t;
          this.changed.emit(t);
          this.drawerService.notifySaved(t);
          this.loadAll();
        },
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo reabrir la tarea.',
          );
          this.confirmDialog.alert({
            title: 'Error',
            message: msg,
            variant: 'danger',
          });
        },
      });
  }

  abrirCancelDialog(): void {
    this.cancelNotes = '';
    this.showCancelDialog = true;
  }

  aplicarCancel(): void {
    if (this.task?.id == null || !this.cancelReasonId) return;
    this.taskApi
      .cancelTask(this.task.id, {
        idTaskCancelReason: this.cancelReasonId,
        cancelNotes: this.cancelNotes || undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (t) => {
          this.task = t;
          this.changed.emit(t);
          this.drawerService.notifySaved(t);
          this.showCancelDialog = false;
          this.loadAll();
          this.toast.success('Tarea cancelada');
        },
        // INC 36 — si ya está cancelada, el backend responde 400; mostrar el message en toast.
        error: (err: any) => {
          const msg = this.taskErrorService.getFriendlyMessage(
            err,
            'No se pudo cancelar la tarea.',
          );
          this.toast.error({ title: 'Error', message: msg });
        },
      });
  }

  agregarComentario(): void {
    if (this.task?.id == null || !this.newCommentContent.trim()) return;
    this.commentApi
      .create(this.task.id, { content: this.newCommentContent.trim() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.newCommentContent = '';
          this.loadComments();
        },
        error: () =>
          this.confirmDialog.alert({
            title: 'Error',
            message: 'No se pudo enviar el comentario.',
            variant: 'danger',
          }),
      });
  }

  async eliminarComentario(c: TaskCommentResponse): Promise<void> {
    if (this.task?.id == null) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar comentario',
      message: '¿Eliminar este comentario? La acción es definitiva.',
    });
    if (!ok) return;
    this.commentApi
      .delete(this.task.id, c.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loadComments();
          this.toast.deleted('Comentario');
        },
        error: () => {
          this.toast.error({ title: 'Error', message: 'No se pudo eliminar.' });
        },
      });
  }

  crearSubtarea(): void {
    if (this.task?.id == null) return;
    // Cerramos el detalle para que el drawer de creación de subtarea sea visible
    // por encima del contenido (evita que quede detrás del modal de detalle).
    this.cerrar();
    this.drawerService.open({
      parentTaskId: this.task.id,
      parentTaskTitle: this.task.title,
      companyId: this.task.intIdCompany,
      dealId: this.task.intIdDeal,
      leadId: this.task.intIdLead,
    });
  }

  abrirSubtarea(s: TaskResponse): void {
    if (s.id == null) return;
    // Mismo criterio: cerrar el detalle antes de abrir el drawer de la subtarea.
    this.cerrar();
    this.drawerService.open({ taskId: s.id });
  }

  cerrar(): void {
    this.closed.emit();
  }

  // ===== Avatar / colores helpers (mismo estilo del contacto) =====

  get initials(): string {
    if (!this.task?.title) return 'T';
    const parts = this.task.title.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'T';
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  /** Color del avatar basado en el tipo de tarea (si tiene), sino derivado del título. */
  get avatarColorClass(): string {
    if (this.task?.type?.color) {
      // Convertimos el HEX a un fondo claro + texto del color → usamos clase brand-like genérica
      return 'text-white shadow-sm';
    }
    const colors = [
      'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
      'bg-error-100 text-error-600 dark:bg-error-900/30 dark:text-error-400',
    ];
    const name = this.task?.title || '';
    const index = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return colors[index % colors.length];
  }

  /** Si hay color de tipo, devolver background-color inline; sino vacío (usa class). */
  get avatarStyle(): { [k: string]: string } {
    if (this.task?.type?.color) {
      return { 'background-color': this.task.type.color };
    }
    return {};
  }

  // ===== Helpers de formato =====

  formatDate(iso?: string): string {
    return this.datetimeService.formatDateTime(iso) || '—';
  }

  isOverdue(t?: TaskResponse): boolean {
    if (!t?.datetimeDue || t?.status?.isFinal) return false;
    return new Date(t.datetimeDue) < new Date();
  }

  /**
   * Detecta si la tarea está cancelada. Prefiere el flag nuevo `isCancelledTerminal` (multi-tenant),
   * con fallback al nombre del status legacy `CANCELLED`.
   * Se ha eliminado la dependencia de idTaskCancelReason y cancelNotes para evitar que el banner
   * persista cuando la tarea se reabre pero el backend conserva las notas de cancelación.
   */
  get isCancelled(): boolean {
    if (!this.task) return false;
    return (
      !!this.task.status?.isCancelledTerminal ||
      this.task.status?.name === 'CANCELLED'
    );
  }

  /** Nombre del motivo de cancelación resuelto desde los catálogos cargados. */
  get cancelReasonName(): string | null {
    const id = this.task?.idTaskCancelReason;
    if (id == null) return null;
    const r = this.cancelReasons.find((x) => x.id === id);
    return r?.label || r?.name || null;
  }

  /** Nombre completo con apellido. */
  formatUserName(
    u: { fullname?: string; lastname?: string } | null | undefined,
  ): string {
    if (!u) return 'Sin información';
    return (
      `${u.fullname ?? ''}${u.lastname ? ' ' + u.lastname : ''}`.trim() ||
      'Sin información'
    );
  }

  /** Color de avatar basado en el nombre (para cards de subtareas/contactos). */
  getEntityAvatarColor(name: string): string {
    const colors = [
      'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400',
      'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
      'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
      'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
      'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
      'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
      'bg-error-100 text-error-600 dark:bg-error-900/30 dark:text-error-400',
    ];
    const idx = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return colors[idx % colors.length];
  }

  getEntityInitials(name?: string | null): string {
    if (!name) return '—';
    return name.substring(0, 2).toUpperCase();
  }

  associationCount(): number {
    let n = 0;
    if (this.task?.intIdCompany) n++;
    if (this.task?.intIdDeal) n++;
    if (this.task?.intIdLead) n++;
    if (this.task?.idParentTask) n++;
    return n;
  }

  /** Primer contacto asociado a la tarea (para prefill de acciones rápidas). */
  get primaryContact(): TaskContactResponse | undefined {
    return this.contacts[0];
  }

  /** Nombre completo del primer contacto asociado, para los chips de acciones rápidas. */
  get primaryContactName(): string {
    const c = this.contacts[0];
    if (!c) return '';
    return `${c.contactFullname ?? ''}${c.contactLastname ? ' ' + c.contactLastname : ''}`.trim();
  }

  // ===== Timeline (historial de actividad) — INC 14 =====

  loadTimeline(page = 0): void {
    if (!this.taskId) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;
    this.auditApi
      .getLogs({
        entityName: 'task',
        idEntity: String(this.taskId),
        page,
        size: 15,
      })
      .subscribe({
        next: (data) => {
          this.timelineLogs = data.content ?? [];
          this.timelinePage = data.page ?? page;
          this.timelineTotalPages = data.totalPages ?? 1;
          this.timelineTotalElements = data.totalElements ?? 0;
          this.loadingTimeline = false;
        },
        error: () => {
          this.timelineError = 'Error al cargar la línea de tiempo.';
          this.loadingTimeline = false;
        },
      });
  }
  /**
   * Abre el detalle del lead asociado. El detalle de un lead es un modal sobre
   * el kanban, por eso se navega a /leads?openLead=<id>: el kanban carga ese
   * lead por id y abre el modal aunque no esté en la página visible (el
   * mecanismo anterior, `state.openLeadId`, solo funcionaba si el lead venía en
   * la primera página de resultados).
   */
  goToLead(idLead: number) {
    this.closed.emit();
    this.router.navigate(['/leads'], { queryParams: { openLead: idLead } });
  }
}
